// What a client tab does to its own grant (#10718): read it, end it, renew
// it, note it. The grant is the one identity (`fp0052`) reports as
// `clientEnvironment` while the tab acts as a staff member's collaborator.
import { useSelector } from "react-redux";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import { clearClientEnvironmentToken } from "@/services/clientEnvironment";
import { RENEW_WINDOW_MINUTES, type StaffGrant } from "@/hooks/useStaffGrant";

export const END_ACCESS_URL = `${BASE_API_URL_V2}/api/v2/client-environment`;
export const RENEW_URL = `${END_ACCESS_URL}/renew`;

/** The client environment as identity reports it, with the note left on
 * this staff member's previous visit to the same client. */
export type ClientEnvironmentGrant = StaffGrant & {
  previousNote?: string | null;
};

type WithGrants = {
  userData?: {
    clientEnvironment?: ClientEnvironmentGrant | null;
    staffGrant?: StaffGrant | null;
  };
};

/** The grant this tab is acting under, or null outside a client. */
export function useClientEnvironment(): ClientEnvironmentGrant | null {
  return useSelector(
    (state: WithGrants) => state.userData?.clientEnvironment ?? null
  );
}

/** The staff member's open grant as their home account sees it, or null. */
export function useOpenStaffGrant(): StaffGrant | null {
  return useSelector((state: WithGrants) => state.userData?.staffGrant ?? null);
}

/** End the grant from inside and drop the token; true when access is over. */
export async function endClientEnvironmentAccess(): Promise<boolean> {
  const response = await fpFetch(END_ACCESS_URL, { method: "DELETE" });
  // 401 or 404: the grant is already over, which is what was asked for.
  if (
    response &&
    (response.ok || response.status === 401 || response.status === 404)
  ) {
    clearClientEnvironmentToken();
    return true;
  }
  return false;
}

/** The server's renew rule: within the last RENEW_WINDOW_MINUTES (on the
 * exact clock, not a rounded countdown), and not already at the cap. */
export function isRenewable(
  grant: Pick<StaffGrant, "expiresAt" | "renewableUntil">,
  now: number = Date.now()
): boolean {
  const expiresMs = new Date(grant.expiresAt).getTime();
  const msLeft = expiresMs - now;
  const atCap =
    grant.renewableUntil !== undefined &&
    expiresMs >= new Date(grant.renewableUntil).getTime();
  return msLeft > 0 && msLeft <= RENEW_WINDOW_MINUTES * 60_000 && !atCap;
}
